<template>
  <div
    class="managers-index"
    role="main"
    aria-label="Gestion des responsables logistiques"
  >
    <!-- Page Header -->
    <div class="page-header mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h2
            id="managers-list-title"
            class="page-title mb-1"
          >
            Responsables logistiques
          </h2>
          <p
            id="managers-list-desc"
            class="page-subtitle text-muted mb-0"
          >
            Liste complète des responsables de dépôts (managers) pour la gestion logistique.
          </p>
        </div>
        <b-button
          :to="{ name: 'manager-add' }"
          variant="primary"
          size="lg"
          class="btn-modern"
          aria-label="Ajouter un nouveau responsable logistique"
        >
          <feather-icon
            icon="PlusIcon"
            size="18"
            class="mr-2"
            aria-hidden="true"
          />
          Nouveau Responsable
        </b-button>
      </div>
    </div>

    <!-- Summary Statistics Cards -->
    <b-row class="mb-4">
      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-3"
      >
        <div class="stats-card stats-card-primary">
          <div class="stats-content">
            <div class="stats-info">
              <h6 class="stats-label">
                Total Managers
              </h6>
              <h2 class="stats-number">
                {{ summaryStats.total || 0 }}
              </h2>
              <div class="stats-subtitle">
                Ce mois
              </div>
            </div>
            <div class="stats-icon">
              <div class="icon-wrapper primary">
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                />
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-3"
      >
        <div class="stats-card stats-card-success">
          <div class="stats-content">
            <div class="stats-info">
              <h6 class="stats-label">
                Managers Actifs
              </h6>
              <h2 class="stats-number">
                {{ summaryStats.active || 0 }}
              </h2>
              <div class="stats-subtitle">
                Disponibles
              </div>
            </div>
            <div class="stats-icon">
              <div class="icon-wrapper success">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="24"
                />
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <b-col
        lg="4"
        md="6"
        sm="12"
        class="mb-3"
      >
        <div class="stats-card stats-card-info">
          <div class="stats-content">
            <div class="stats-info">
              <h6 class="stats-label">
                Dépôts Couverts
              </h6>
              <h2 class="stats-number">
                {{ summaryStats.depots || 0 }}
              </h2>
              <div class="stats-subtitle">
                Déploiement
              </div>
            </div>
            <div class="stats-icon">
              <div class="icon-wrapper info">
                <feather-icon
                  icon="MapPinIcon"
                  size="24"
                />
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Modern Search and Filters -->
    <div class="search-filter-section mb-4">
      <div
        class="search-filter-card"
        aria-label="Recherche et filtres pour responsables logistiques"
      >
        <div class="card-header">
          <h5
            id="search-filters-title"
            class="card-title mb-0"
          >
            <feather-icon
              icon="SearchIcon"
              size="18"
              class="mr-2"
              aria-hidden="true"
            />
            Recherche et Filtres
          </h5>
        </div>
        <div class="card-body">
          <b-row class="align-items-end">
            <!-- Enhanced Search -->
            <b-col
              md="4"
              sm="12"
              class="mb-3"
            >
              <label
                class="form-label"
                for="manager-search"
              >Recherche Globale</label>
              <div class="search-input-wrapper">
                <div class="search-icon">
                  <feather-icon
                    icon="SearchIcon"
                    size="16"
                    aria-hidden="true"
                  />
                </div>
                <b-form-input
                  id="manager-search"
                  v-model="searchTerm"
                  placeholder="Nom, email, téléphone..."
                  class="premium-input search-input"
                  aria-label="Recherche globale sur les responsables logistiques"
                  aria-describedby="manager-search-desc"
                  @input="onSearchChange"
                />
                <div
                  v-if="searchTerm"
                  class="clear-search"
                  aria-label="Effacer la recherche"
                  role="button"
                  tabindex="0"
                  @click="clearSearch"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                    aria-hidden="true"
                  />
                </div>
                <small
                  id="manager-search-desc"
                  class="sr-only"
                >Saisissez un nom, une adresse e-mail ou un numéro de téléphone pour filtrer la liste des responsables logistiques.</small>
              </div>
            </b-col>

            <!-- Repository Filter -->
            <b-col
              md="3"
              sm="12"
              class="mb-3"
            >
              <label
                class="form-label"
                for="repository-filter"
              >Entrepôt / Dépôt</label>
              <b-form-select
                id="repository-filter"
                v-model="repositoryFilter"
                :options="repositorySelectOptions"
                class="modern-native-select"
                aria-label="Filtrer par dépôt logistique"
                aria-describedby="repository-filter-desc"
                @change="getManagers"
              >
                <template #first>
                  <b-form-select-option :value="null">
                    🏢 Tous les entrepôts
                  </b-form-select-option>
                </template>
              </b-form-select>
              <small
                id="repository-filter-desc"
                class="sr-only"
              >Sélectionnez un dépôt pour filtrer les responsables associés à ce site logistique.</small>
            </b-col>

            <!-- Status Filter -->
            <b-col
              md="3"
              sm="12"
              class="mb-3"
            >
              <label
                class="form-label"
                for="status-filter"
              >État du Responsable</label>
              <b-form-select
                id="status-filter"
                v-model="statusFilter"
                :options="statusSelectOptions"
                class="modern-native-select"
                aria-label="Filtrer par état du responsable logistique"
                aria-describedby="status-filter-desc"
                @change="getManagers"
              />
              <small
                id="status-filter-desc"
                class="sr-only"
              >Filtrez les responsables logistiques selon leur statut d'activité.</small>
            </b-col>

            <!-- Action Buttons -->
            <b-col
              md="2"
              sm="12"
              class="mb-3"
            >
              <div class="action-buttons-group">

                <b-dropdown
                  variant="outline-primary"
                  class="btn-modern-outline ml-2"
                  text="Options"
                  size="sm"
                  aria-label="Options d'affichage et d'export"
                >
                  <b-dropdown-item
                    aria-label="Exporter la liste des responsables logistiques au format Excel"
                    @click="exportToCSV"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      size="16"
                      class="mr-2"
                      aria-hidden="true"
                    />
                    Télécharger Excel
                  </b-dropdown-item>
                </b-dropdown>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
    </div>

    <!-- Modern Loading State -->
    <div
      v-if="isLoading"
      class="loading-container"
      aria-live="polite"
      aria-busy="true"
      role="status"
    >
      <div class="loading-content">
        <div
          class="modern-spinner"
          aria-hidden="true"
        >
          <div class="spinner-ring" />
          <div class="spinner-ring" />
          <div class="spinner-ring" />
        </div>
        <p class="loading-text mt-3">
          Chargement des responsables logistiques...
        </p>
      </div>
    </div>

    <!-- Enhanced Card View -->
    <div
      v-else-if="viewMode === 'card' && !isLoading"
      class="managers-grid"
      aria-label="Vue cartes des responsables logistiques"
    >
      <transition-group
        name="card-fade"
        tag="div"
        class="row"
      >
        <div
          v-for="manager in managers"
          :key="manager.id"
          class="col-lg-4 col-md-6 col-sm-12 mb-4"
        >
          <div class="manager-card-modern">
            <!-- Card Header -->
            <div class="card-header-modern">
              <div class="manager-avatar">
                <div
                  class="avatar-circle"
                  :aria-label="'Avatar de ' + (manager.username || 'Sans nom')"
                  role="img"
                >
                  <span class="avatar-initials">{{ getInitials(manager.username) }}</span>
                </div>
                <div
                  class="status-indicator"
                  :class="{ active: manager.is_active }"
                  :aria-label="manager.is_active ? 'Responsable actif' : 'Responsable inactif'"
                  role="status"
                />
              </div>
              <div class="manager-status">
                <span
                  class="status-badge"
                  :class="manager.is_active ? 'active' : 'inactive'"
                  :aria-label="manager.is_active ? 'Actif' : 'Inactif'"
                >
                  {{ manager.is_active ? 'Actif' : 'Inactif' }}
                </span>
              </div>
            </div>

            <!-- Card Body -->
            <div class="card-body-modern">
              <h5 class="manager-name">
                {{ manager.username || 'Sans nom' }}
              </h5>

              <div class="manager-details">
                <div class="detail-item">
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                    class="detail-icon"
                  />
                  <span class="detail-text">{{ manager.email || 'N/A' }}</span>
                </div>
                <div class="detail-item">
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                    class="detail-icon"
                  />
                  <span class="detail-text">{{ manager.phone || 'N/A' }}</span>
                </div>
                <div class="detail-item">
                  <feather-icon
                    icon="PhoneForwardedIcon"
                    size="14"
                    class="detail-icon"
                  />
                  <span class="detail-text">{{ manager.phone2 || 'N/A' }}</span>
                </div>
              </div>

              <!-- Repository Assignment -->
              <div class="repository-assignment">
                <div class="assignment-info">
                  <span class="assignment-label">Dépôt Assigné</span>
                  <div class="repository-display">
                    <div class="repository-badge">
                      <feather-icon
                        icon="HomeIcon"
                        size="16"
                        class="repository-icon"
                      />
                      <span class="repository-name">{{ manager.repository_name || 'Aucun dépôt assigné' }}</span>
                    </div>
                    <div
                      v-if="manager.repository_name"
                      class="assignment-status active"
                    >
                      <feather-icon
                        icon="CheckCircleIcon"
                        size="12"
                        class="mr-1"
                      />
                      Assigné
                    </div>
                    <div
                      v-else
                      class="assignment-status inactive"
                    >
                      <feather-icon
                        icon="AlertCircleIcon"
                        size="12"
                        class="mr-1"
                      />
                      Non assigné
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card Actions -->
            <div class="card-actions-modern">
              <b-button
                variant="outline-primary"
                size="sm"
                class="action-btn-text view-btn"
                :to="{ name: 'manager-show', params: { id: manager.id }}"
                :aria-label="`Voir le détail du responsable logistique ${manager.username}`"
              >
                <feather-icon
                  icon="EyeIcon"
                  size="14"
                  class="mr-1"
                  aria-hidden="true"
                />
                Voir
              </b-button>
              <b-button
                variant="outline-warning"
                size="sm"
                class="action-btn-text edit-btn"
                :to="{ name: 'manager-edit', params: { id: manager.id }}"
                :aria-label="`Modifier le responsable logistique ${manager.username}`"
              >
                <feather-icon
                  icon="EditIcon"
                  size="14"
                  class="mr-1"
                  aria-hidden="true"
                />
                Modifier
              </b-button>
              <b-button
                v-if="user && user.role === 'admin'"
                v-b-tooltip.hover
                title="Changer le mot de passe"
                variant="outline-warning"
                size="sm"
                class="action-btn-text"
                :aria-label="`Changer le mot de passe de ${manager.username}`"
                @click="openPasswordChangeModal(manager)"
              >
                <feather-icon
                  icon="LockIcon"
                  size="14"
                  class="mr-1"
                  aria-hidden="true"
                />
                Mot de passe
              </b-button>
              <b-button
                :variant="manager.is_active ? 'outline-secondary' : 'outline-success'"
                size="sm"
                class="action-btn-text toggle-btn"
                :aria-label="manager.is_active ? 'Désactiver le responsable logistique ' + manager.username : 'Activer le responsable logistique ' + manager.username"
                @click="toggleManagerStatus(manager)"
              >
                <feather-icon
                  :icon="manager.is_active ? 'UserXIcon' : 'UserCheckIcon'"
                  size="14"
                  class="mr-1"
                  aria-hidden="true"
                />
                {{ manager.is_active ? 'Désactiver' : 'Activer' }}
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="action-btn-text delete-btn"
                :aria-label="'Supprimer le responsable logistique ' + manager.username"
                @click="deleteManager(manager.id)"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="14"
                  class="mr-1"
                  aria-hidden="true"
                />
                Supprimer
              </b-button>
            </div>
          </div>
        </div>
      </transition-group>
    </div>

    <!-- Enhanced Empty State -->
    <div
      v-if="!isLoading && managers.length === 0"
      class="empty-state"
      aria-live="polite"
      role="status"
    >
      <div class="empty-state-content">
        <div class="empty-state-illustration">
          <div class="illustration-circle">
            <feather-icon
              icon="UsersIcon"
              size="48"
              class="illustration-icon"
              aria-hidden="true"
            />
          </div>
        </div>
        <h3 class="empty-state-title">
          Aucun responsable logistique trouvé
        </h3>
        <p class="empty-state-description">
          Il semble qu'aucun responsable ne corresponde à vos critères de recherche.<br>
          Essayez d'ajuster vos filtres ou ajoutez un nouveau responsable logistique.
        </p>
        <div class="empty-state-actions">
          <b-button
            :to="{ name: 'manager-add' }"
            variant="primary"
            size="lg"
            class="btn-modern mr-3"
            aria-label="Ajouter un nouveau responsable logistique"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
              class="mr-2"
              aria-hidden="true"
            />
            Ajouter un Responsable
          </b-button>
          <b-button
            variant="outline-secondary"
            size="lg"
            class="btn-modern-outline"
            aria-label="Réinitialiser les filtres de recherche"
            @click="resetFilters"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              class="mr-2"
              aria-hidden="true"
            />
            Réinitialiser les Filtres
          </b-button>
        </div>
      </div>
    </div>

    <!-- Password Change Modal -->
    <b-modal
      id="password-change-modal-manager"
      v-model="showPasswordChangeModal"
      centered
      hide-footer
      size="lg"
      title-tag="div"
      @hidden="resetPasswordChangeModal"
    >
    <template #modal-header="{ close }">
      <div class="w-100">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex align-items-center">
            <div class="password-icon-wrapper-modal mr-2">
              <feather-icon
                icon="LockIcon"
                size="20"
                class="text-primary"
              />
            </div>
            <div>
              <h5 class="mb-0">
                Changer le mot de passe
              </h5>
              <small
                v-if="selectedManagerForPassword"
                class="text-muted"
              >
                {{ selectedManagerForPassword.username }} • {{ selectedManagerForPassword.email }}
              </small>
            </div>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="btn-icon"
            @click="close"
          >
            <feather-icon icon="XIcon" />
          </b-button>
        </div>
      </div>
    </template>

    <div v-if="selectedManagerForPassword">
      <validation-observer
        ref="passwordFormValidator"
        v-slot="{ handleSubmit }"
      >
        <b-form @submit.prevent="handleSubmit(savePasswordChange)">
          <!-- Password Input -->
          <b-row>
            <b-col cols="12">
              <b-form-group
                label="Nouveau mot de passe *"
                label-for="modal-password"
                label-class="form-label-premium"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Mot de passe"
                  rules="required|min:8"
                  vid="modal-password"
                >
                  <b-input-group class="premium-input-group">
                    <b-form-input
                      id="modal-password"
                      v-model="modalPasswordForm.password"
                      :type="showModalPassword ? 'text' : 'password'"
                      :state="errors.length > 0 ? false : (passwordStrength > 25 ? true : null)"
                      placeholder="Entrez un mot de passe sécurisé"
                      class="premium-input"
                      @input="onPasswordInput"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="outline-secondary"
                        class="password-toggle-btn"
                        @click="showModalPassword = !showModalPassword"
                      >
                        <feather-icon
                          :icon="showModalPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          size="16"
                        />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                  <small class="text-danger">{{ errors[0] }}</small>

                  <!-- Password Strength Indicator -->
                  <div
                    v-if="modalPasswordForm.password"
                    class="password-strength-wrapper mt-2"
                  >
                    <div class="d-flex justify-content-between mb-1">
                      <small class="strength-label">Force du mot de passe :</small>
                      <small
                        class="strength-text"
                        :class="passwordStrengthClass"
                      >{{ passwordStrengthText }}</small>
                    </div>
                    <b-progress
                      :value="passwordStrength"
                      :variant="passwordStrengthClass"
                      max="100"
                      height="6px"
                      class="premium-progress"
                    />
                  </div>
                </validation-provider>
              </b-form-group>
            </b-col>

            <!-- Confirm Password Input -->
            <b-col cols="12">
              <b-form-group
                label="Confirmer le mot de passe *"
                label-for="modal-confirm-password"
                label-class="form-label-premium"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Confirmation du mot de passe"
                  rules="required|confirmed:modal-password"
                >
                  <b-input-group class="premium-input-group">
                    <b-form-input
                      id="modal-confirm-password"
                      v-model="modalPasswordForm.confirm_password"
                      :type="showModalConfirmPassword ? 'text' : 'password'"
                      :state="getConfirmPasswordState(errors)"
                      placeholder="Confirmez le mot de passe"
                      class="premium-input"
                      @input="onPasswordInput"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="outline-secondary"
                        class="password-toggle-btn"
                        @click="showModalConfirmPassword = !showModalConfirmPassword"
                      >
                        <feather-icon
                          :icon="showModalConfirmPassword ? 'EyeOffIcon' : 'EyeIcon'"
                          size="16"
                        />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>

                  <!-- Real-time validation feedback -->
                  <div
                    v-if="modalPasswordForm.confirm_password"
                    class="mt-1"
                  >
                    <small
                      v-if="passwordsMatch && modalPasswordForm.password"
                      class="text-success d-flex align-items-center validation-message-animate"
                    >
                      <feather-icon
                        icon="CheckCircleIcon"
                        size="14"
                        class="mr-50"
                      />
                      Les mots de passe correspondent
                    </small>
                    <small
                      v-else-if="modalPasswordForm.password"
                      class="text-danger d-flex align-items-center validation-message-animate"
                    >
                      <feather-icon
                        icon="XCircleIcon"
                        size="14"
                        class="mr-50"
                      />
                      Les mots de passe ne correspondent pas
                    </small>
                    <small
                      v-else
                      class="text-warning d-flex align-items-center validation-message-animate"
                    >
                      <feather-icon
                        icon="AlertTriangleIcon"
                        size="14"
                        class="mr-50"
                      />
                      Veuillez d'abord saisir un mot de passe
                    </small>
                  </div>
                  <small
                    v-if="errors.length > 0 && !modalPasswordForm.confirm_password"
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </validation-provider>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Generate Password Button -->
          <div class="text-center mb-3 mt-2">
            <PasswordGenerator
              :user="selectedManagerForPassword"
              user-type="manager"
              @generated="onPasswordGenerated"
            />
          </div>

          <!-- Action Buttons -->
          <div class="d-flex justify-content-between mt-4">
            <b-button
              variant="outline-secondary"
              class="premium-btn-secondary"
              @click="closePasswordChangeModal"
            >
              <feather-icon
                icon="XIcon"
                size="16"
                class="mr-50"
              />
              Annuler
            </b-button>
            <span id="password-save-btn-wrapper" class="d-inline-block">
              <b-button
                type="submit"
                variant="primary"
                class="premium-btn-primary save-btn-animated"
                :class="{ 'btn-saving': isSavingPassword, 'not-allowed': !canSavePassword || isSavingPassword }"
                :disabled="!canSavePassword || isSavingPassword"
              >
                <b-spinner
                  v-if="isSavingPassword"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  size="16"
                  class="mr-50"
                />
                Enregistrer
              </b-button>
              <b-tooltip
                v-if="!canSavePassword || isSavingPassword"
                target="password-save-btn-wrapper"
                triggers="hover focus"
                placement="top"
              >
                <template v-if="isSavingPassword">
                  Enregistrement en cours...
                </template>
                <template v-else-if="!modalPasswordForm.password">
                  Veuillez saisir un mot de passe
                </template>
                <template v-else-if="modalPasswordForm.password.length < 8">
                  Le mot de passe doit contenir au moins 8 caractères
                </template>
                <template v-else-if="!modalPasswordForm.confirm_password">
                  Veuillez confirmer le mot de passe
                </template>
                <template v-else-if="!passwordsMatch">
                  Les mots de passe ne correspondent pas
                </template>
                <template v-else>
                  Veuillez remplir correctement les champs
                </template>
              </b-tooltip>
            </span>
          </div>
        </b-form>
      </validation-observer>
    </div>
  </b-modal>
</div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import PasswordGenerator from '@core/components/password/PasswordGenerator.vue'
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { required, confirmed, min } from 'vee-validate/dist/rules'
import storeAuth from '@/store/store'
import {
  BButton,
  BFormInput,
  BFormSelect,
  BRow,
  BCol,
  BDropdown,
  BDropdownItem,
  BModal,
  BForm,
  BFormGroup,
  BInputGroup,
  BInputGroupAppend,
  BProgress,
  BTooltip,
  BSpinner,
} from 'bootstrap-vue'

// Register validation rules
extend('required', required)
extend('confirmed', confirmed)
extend('min', min)

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    PasswordGenerator,
    BButton,
    BFormInput,
    BFormSelect,
    BRow,
    BCol,
    BDropdown,
    BDropdownItem,
    BModal,
    BForm,
    BFormGroup,
    BInputGroup,
    BInputGroupAppend,
    BProgress,
    BTooltip,
    BSpinner,
  },

  data() {
    return {
      columns: [
        { label: 'Manager', field: 'username', sortable: true },
        { label: 'Email', field: 'email', sortable: true },
        { label: 'téléphone professionnel', field: 'phone', sortable: true },
        { label: 'téléphone personnel', field: 'phone2', sortable: true },
        { label: 'Entrepôt', field: 'repository', sortable: true },
        { label: 'État', field: 'is_active', sortable: true },
        {
          label: 'Actions', field: 'action', sortable: false, width: '220px',
        },
      ],
      managers: [],
      searchTerm: '',
      isLoading: false,
      pageLength: 10,
      viewMode: 'card', // Default to card view for modern UI
      repositories: [],
      repositoryFilter: null,
      statusFilter: null,
      statusOptions: [
        { label: 'Tous les statuts', value: null },
        { label: 'Actif', value: true },
        { label: 'Inactif', value: false },
      ],
      summaryStats: {
        total: 0,
        active: 0,
        depots: 0,
      },
      searchTimer: null,
      user: storeAuth.state.user, // Current logged-in user
      // Password change modal
      showPasswordChangeModal: false,
      selectedManagerForPassword: null,
      modalPasswordForm: {
        password: '',
        confirm_password: '',
      },
      showModalPassword: false,
      showModalConfirmPassword: false,
      isSavingPassword: false,
    }
  },

  computed: {
    repositorySelectOptions() {
      return this.repositories.map(repo => ({
        value: repo.id,
        text: `🏢 ${repo.name}`,
      }))
    },

    statusSelectOptions() {
      return [
        { value: null, text: '📋 Tous les états' },
        { value: true, text: '✅ Managers Actifs' },
        { value: false, text: '❌ Managers Inactifs' },
      ]
    },

    // Password validation computed properties
    passwordStrength() {
      let strength = 0
      const { password } = this.modalPasswordForm

      if (!password) return 0

      if (password.length >= 8) strength += 25
      if (/[A-Z]/.test(password)) strength += 25
      if (/[0-9]/.test(password)) strength += 25
      if (/[^A-Za-z0-9]/.test(password)) strength += 25

      return strength
    },

    passwordStrengthClass() {
      if (this.passwordStrength <= 25) return 'danger'
      if (this.passwordStrength <= 50) return 'warning'
      if (this.passwordStrength <= 75) return 'info'
      return 'success'
    },

    passwordStrengthText() {
      if (this.passwordStrength <= 25) return 'Faible'
      if (this.passwordStrength <= 50) return 'Moyen'
      if (this.passwordStrength <= 75) return 'Bon'
      return 'Fort'
    },

    passwordsMatch() {
      return (
        this.modalPasswordForm.password
        && this.modalPasswordForm.confirm_password
        && this.modalPasswordForm.password === this.modalPasswordForm.confirm_password
      )
    },

    canSavePassword() {
      return (
        this.modalPasswordForm.password
        && this.modalPasswordForm.confirm_password
        && this.passwordsMatch
        && this.modalPasswordForm.password.length >= 8
        && !this.isSavingPassword
      )
    },
  },

  created() {
    this.getManagers()
    this.fetchRepositories()
  },

  methods: {
    async getManagers() {
      this.isLoading = true
      try {
        // Enhanced API call with filters
        const params = {}
        if (this.repositoryFilter) {
          params.repository = this.repositoryFilter
        }
        if (this.searchTerm) {
          params.search = this.searchTerm
        }
        if (this.statusFilter !== null) {
          params.is_active = this.statusFilter
        }

        const { data } = await axios.get('/api/auth/users/managers/', { params })
        // Support paginated and non-paginated responses
        this.managers = Array.isArray(data) ? data : (data.results || [])
        this.calculateSummaryStats()
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des managers')
      } finally {
        this.isLoading = false
      }
    },

    async fetchRepositories() {
      try {
        const { data } = await axios.get('/api/repositories/')
        this.repositories = data
      } catch (error) {
        this.showToast('danger', 'top-center', 'Erreur lors du chargement des dépôts')
      }
    },

    calculateSummaryStats() {
      this.summaryStats = {
        total: this.managers.length,
        active: this.managers.filter(s => s.is_active).length,
        depots: new Set(this.managers.map(m => m.repository).filter(Boolean)).size,
      }
    },

    async deleteManager(id) {
      try {
        const result = await this.$swal({
          title: 'Êtes-vous sûr ?',
          text: 'Cette action supprimera définitivement ce manager.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Oui, supprimer',
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        if (result.value) {
          this.isLoading = true
          try {
            await axios.put(`/api/auth/users/delete/${id}/`)
            await this.getManagers()
            this.showToast('success', 'top-center', 'Manager supprimé avec succès')
          } catch (error) {
            this.showToast('danger', 'top-center', error.toString())
          } finally {
            this.isLoading = false
          }
        }
      } catch (e) {
        // Modal dismissed, do nothing
      }
    },

    async toggleManagerStatus(manager) {
      const action = manager.is_active ? 'désactiver' : 'activer'
      const status = manager.is_active ? 'désactivé' : 'activé'

      try {
        const result = await this.$swal({
          title: `${action.charAt(0).toUpperCase() + action.slice(1)} ce manager ?`,
          text: `Cette action va ${action} le manager ${manager.username}.`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: `Oui, ${action}`,
          cancelButtonText: 'Annuler',
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1',
          },
          buttonsStyling: false,
        })
        if (result.value) {
          this.isLoading = true
          try {
            await axios.post(`/api/auth/users/managers/toggle-activation/${manager.id}/`)
            await this.getManagers()
            this.showToast('success', 'top-center', `Manager ${status} avec succès`)
          } catch (error) {
            this.showToast('danger', 'top-center', `Erreur lors de la ${action}`)
          } finally {
            this.isLoading = false
          }
        }
      } catch (e) {
        // Modal dismissed, do nothing
      }
    },

    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: variant === 'success' ? 'Succès' : 'Erreur',
            icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
            text,
            variant,
          },
        },
        { position },
      )
    },

    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'card' : 'table'
    },

    resetFilters() {
      this.searchTerm = ''
      this.repositoryFilter = null
      this.statusFilter = null
      this.getManagers()
    },

    onSearchChange() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.getManagers()
      }, 500)
    },

    clearSearch() {
      this.searchTerm = ''
      this.getManagers()
    },

    refreshData() {
      this.getManagers()
      this.fetchRepositories()
    },

    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
    },

    exportToCSV() {
      try {
        // Format data for CSV
        const csvData = this.managers.map(manager => ({
          Nom: manager.username || '',
          Email: manager.email || '',
          'téléphone professionnel': manager.phone || '',
          'téléphone personnel': manager.phone2 || '', // <-- Add this line
          Dépôt: manager.repository_name || '',
          Statut: manager.is_active ? 'Actif' : 'Inactif',
        }))

        // Convert to CSV string
        const headers = Object.keys(csvData[0]).join(',')
        const rows = csvData.map(obj => Object.values(obj).map(val => `"${val}"`).join(','))
        const csvContent = [headers, ...rows].join('\n')

        // Create blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        link.setAttribute('href', url)
        link.setAttribute('download', `managers_${new Date().toISOString().slice(0, 10)}.csv`)
        link.style.visibility = 'hidden'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.showToast('success', 'top-right', 'Données exportées avec succès')
      } catch (error) {
        this.showToast('danger', 'top-right', "Erreur lors de l'exportation")
      }
    },

    // Password change methods
    openPasswordChangeModal(manager) {
      this.selectedManagerForPassword = manager
      this.showPasswordChangeModal = true
    },

    closePasswordChangeModal() {
      this.showPasswordChangeModal = false
    },

    resetPasswordChangeModal() {
      this.modalPasswordForm = {
        password: '',
        confirm_password: '',
      }
      this.showModalPassword = false
      this.showModalConfirmPassword = false
      this.isSavingPassword = false
      this.selectedManagerForPassword = null
      if (this.$refs.passwordFormValidator) {
        this.$refs.passwordFormValidator.reset()
      }
    },

    onPasswordGenerated(generatedPassword) {
      this.modalPasswordForm.password = generatedPassword
      this.modalPasswordForm.confirm_password = generatedPassword
      this.showModalPassword = true
      this.showModalConfirmPassword = true
    },

    onPasswordInput() {
      // Trigger validation on input
      if (this.$refs.passwordFormValidator) {
        this.$refs.passwordFormValidator.validate({ silent: false })
      }
    },

    getConfirmPasswordState(errors) {
      if (errors.length > 0) return false
      if (this.modalPasswordForm.confirm_password && this.passwordsMatch) return true
      if (this.modalPasswordForm.confirm_password && !this.passwordsMatch) return false
      return null
    },

    async savePasswordChange() {
      if (!this.canSavePassword || !this.selectedManagerForPassword) return

      this.isSavingPassword = true
      try {
        await axios.post(
          `/api/auth/users/managers/change-password/${this.selectedManagerForPassword.id}/`,
          this.modalPasswordForm,
        )

        this.showToast('success', 'top-right', 'Mot de passe modifié avec succès')
        this.closePasswordChangeModal()
        this.resetPasswordChangeModal()
      } catch (error) {
        const errorMsg = error.response?.data?.error
          || error.response?.data?.detail
          || 'Erreur lors du changement de mot de passe'
        this.showToast('danger', 'top-right', errorMsg)
      } finally {
        this.isSavingPassword = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Modern Managers Index Styling with Team Parcel Express Branding
.managers-index {
  padding: 0;
  background: #ffffff;
  min-height: 100vh;
  padding: 2rem;
}

// Page Header Styling
.page-header {
  .page-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  .page-subtitle {
    font-size: 1.1rem;
    color: #6c757d;
  }

  .btn-modern {
    background: linear-gradient(135deg, #C62828 0%, #0a3370 100%);
    border: none;
    border-radius: 12px;
    padding: 0.75rem 2rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 8px 25px rgba(255, 159, 67, 0.3);
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 35px rgba(255, 159, 67, 0.4);
      background: linear-gradient(135deg, #0a3370 0%, #0a3370 100%);
    }
  }
}

// Modern Stats Cards
.stats-card {
  background: white;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    display: none;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 25px rgba(0, 0, 0, 0.08);
  }

  &.stats-card-primary {
    background: rgba(255, 159, 67, 0.06);
    border-left: 4px solid #C62828;
  }

  &.stats-card-success {
    background: rgba(22, 163, 74, 0.06);
    border-left: 4px solid #16a34a;
  }

  &.stats-card-info {
    background: rgba(31, 41, 55, 0.04);
    border-left: 4px solid #1f2937;
  }

  .stats-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
  }

  .stats-info {
    flex: 1;
  }

  .stats-label {
    font-size: 0.7rem;
    color: #6e6b7b;
    font-weight: 600;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }  .stats-number {
    font-size: 1.6rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.25rem;
    line-height: 1;
  }

  .stats-subtitle {
    font-size: 0.75rem;
    color: #b9b9c3;
  }

  .stats-change {
    .change-indicator {
      font-size: 0.8rem;
      font-weight: 600;
      padding: 0.25rem 0.5rem;
      border-radius: 20px;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;

      &.positive {
        background: rgba(22, 163, 74, 0.1);
        color: #16a34a;
      }

      &.negative {
        background: rgba(255, 159, 67, 0.1);
        color: #C62828;
      }

      &.neutral {
        background: rgba(55, 65, 81, 0.1);
        color: #374151;
      }
    }
  }

  .stats-icon {
    .icon-wrapper {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;

      &.primary {
        background: linear-gradient(135deg, rgba(255, 159, 67, 0.1) 0%, rgba(255, 140, 0, 0.1) 100%);
        color: #C62828;
      }

      &.success {
        background: linear-gradient(135deg, rgba(22, 163, 74, 0.1) 0%, rgba(21, 128, 61, 0.1) 100%);
        color: #16a34a;
      }

      &.info {
        background: linear-gradient(135deg, rgba(55, 65, 81, 0.1) 0%, rgba(31, 41, 55, 0.1) 100%);
        color: #374151;
      }
    }
  }

  .stats-progress {
    height: 4px;
    background: #f1f2f6;
    border-radius: 2px;
    overflow: hidden;

    .progress-bar {
      height: 100%;
      border-radius: 2px;
      transition: width 0.3s ease;

      &.primary {
        background: linear-gradient(135deg, #C62828 0%, #0a3370 100%);
      }

      &.success {
        background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
      }

      &.info {
        background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
      }
    }
  }
}

// Search and Filter Section
.search-filter-section {
  .search-filter-card {
    background: white;
    border-radius: 20px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    .card-header {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      padding: 1.5rem 2rem;
      border-bottom: 1px solid #e9ecef;    .card-title {
      font-weight: 600;
      color: #1f2937;
      display: flex;
      align-items: center;
    }
    }

    .card-body {
      padding: 2rem;
    }
  }

  .form-label {
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .search-input-wrapper {
    position: relative;

    .search-icon {
      position: absolute;
      left: 1rem;
      top: 50%;
      transform: translateY(-50%);
      color: #8b92b2;
      z-index: 2;
    }

    .search-input {
      padding-left: 2.5rem;
      padding-right: 2.5rem;
      border: 2px solid #e9ecef;
      border-radius: 12px;
      font-size: 0.95rem;
      transition: all 0.3s ease;
      background: #f8f9fa;

      &:focus {
        border-color: #C62828;
        box-shadow: 0 0 0 0.2rem rgba(255, 159, 67, 0.25);
        background: white;
      }
    }

    .clear-search {
      position: absolute;
      right: 1rem;
      top: 50%;
      transform: translateY(-50%);
      color: #8b92b2;
      cursor: pointer;
      transition: color 0.3s ease;

      &:hover {
        color: #C62828;
      }
    }
  }

  // Modern Native Select Styling
  .modern-native-select {
    border: 2px solid #e9ecef;
    border-radius: 12px;
    background: #f8f9fa;
    min-height: 48px;
    transition: all 0.3s ease;
    font-size: 0.95rem;
    padding: 0.75rem 1rem;

    &:focus {
      border-color: #C62828;
      box-shadow: 0 0 0 0.2rem rgba(255, 159, 67, 0.25);
      background: white;
      outline: none;
    }

    option {
      padding: 0.5rem;
      font-size: 0.9rem;
    }
  }

  .action-buttons-group {
    display: flex;
    gap: 0.5rem;
  }

  .btn-modern-outline {
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 0.75rem 1rem;
    transition: all 0.3s ease;
    background: white;

    &:hover {
      color: white;
    }
  }
}

// Loading Animation
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem 0;

  .loading-content {
    text-align: center;
  }

  .modern-spinner {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;

    .spinner-ring {
      box-sizing: border-box;
      display: block;
      position: absolute;
      width: 64px;
      height: 64px;
      margin: 8px;
      border: 8px solid #C62828;
      border-radius: 50%;
      animation: spinner-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
      border-color: #C62828 transparent transparent transparent;

      &:nth-child(1) { animation-delay: -0.45s; }
      &:nth-child(2) { animation-delay: -0.3s; }
      &:nth-child(3) { animation-delay: -0.15s; }
    }
  }

  .loading-text {
    color: #8b92b2;
    font-weight: 500;
  }
}

@keyframes spinner-ring {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

// Modern Card Grid
.managers-grid {
  .card-fade-enter-active,
  .card-fade-leave-active {
    transition: all 0.3s ease;
  }

  .card-fade-enter,
  .card-fade-leave-to {
    opacity: 0;
    transform: translateY(20px);
  }
}

// Enhanced Manager Cards
.manager-card-modern {
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  }

  .card-header-modern {
    padding: 1.5rem;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e9ecef;

    .manager-avatar {
      position: relative;

      .avatar-circle {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: linear-gradient(135deg, #C62828 0%, #0a3370 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 8px 25px rgba(255, 159, 67, 0.3);

        .avatar-initials {
          color: white;
          font-weight: 700;
          font-size: 1.2rem;
        }
      }

      .status-indicator {
        position: absolute;
        bottom: 2px;
        right: 2px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 3px solid white;
        background: #C62828;

        &.active {
          background: #16a34a;
        }
      }
    }

    .manager-status {
      .status-badge {
        padding: 0.4rem 1rem;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &.active {
          background: rgba(22, 163, 74, 0.1);
          color: #16a34a;
        }

        &.inactive {
          background: rgba(255, 159, 67, 0.1);
          color: #C62828;
        }
      }
    }
  }

  .card-body-modern {
    padding: 1.5rem;

    .manager-name {
      font-size: 1.25rem;
      font-weight: 700;
      color: #1f2937;
      margin-bottom: 1rem;
    }

    .manager-details {
      margin-bottom: 1.5rem;

      .detail-item {
        display: flex;
        align-items: center;
        margin-bottom: 0.75rem;

        .detail-icon {
          color: #8b92b2;
          margin-right: 0.75rem;
          flex-shrink: 0;
        }

        .detail-text {
          color: #6c757d;
          font-size: 0.9rem;
          flex: 1;
        }
      }
    }

    .repository-assignment {
      .assignment-info {
        margin-bottom: 1rem;

        .assignment-label {
          font-size: 0.8rem;
          color: #8b92b2;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.75rem;
          display: block;
        }

        .repository-display {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;

          .repository-badge {
            display: flex;
            align-items: center;
            padding: 0.75rem 1rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border: 1px solid #dee2e6;
            border-radius: 12px;
            transition: all 0.3s ease;

            .repository-icon {
              color: #C62828;
              margin-right: 0.75rem;
              flex-shrink: 0;
            }

            .repository-name {
              color: #1f2937;
              font-weight: 600;
              font-size: 0.9rem;
              flex: 1;
            }

            &:hover {
              background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
          }

          .assignment-status {
            display: flex;
            align-items: center;
            font-size: 0.75rem;
            font-weight: 600;
            padding: 0.375rem 0.75rem;
            border-radius: 20px;
            align-self: flex-start;

            &.active {
              background: rgba(22, 163, 74, 0.1);
              color: #16a34a;
              border: 1px solid rgba(22, 163, 74, 0.2);
            }

            &.inactive {
              background: rgba(245, 101, 101, 0.1);
              color: #ef4444;
              border: 1px solid rgba(245, 101, 101, 0.2);
            }
          }
        }
      }
    }
  }

  .card-actions-modern {
    padding: 1rem 1.5rem;
    background: #f8f9fa;
    display: flex;
    justify-content: center;
    gap: 0.75rem;

    .action-btn-text {
      border-radius: 8px;
      padding: 0.5rem 1rem;
      font-size: 0.8rem;
      font-weight: 600;
      transition: all 0.3s ease;
      text-decoration: none;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
      }

      &.toggle-btn {
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }
      }
    }

    // Keep old circular button styles for backward compatibility
    .action-btn {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      text-decoration: none;

      &.view-btn {
        background: rgba(255, 159, 67, 0.1);
        color: #C62828;

        &:hover {
          background: #C62828;
          color: white;
          transform: scale(1.1);
        }
      }

      &.edit-btn {
        background: rgba(245, 158, 11, 0.1);
        color: #f59e0b;

        &:hover {
          background: #f59e0b;
          color: white;
          transform: scale(1.1);
        }
      }

      &.delete-btn {
        background: rgba(107, 114, 128, 0.1);
        color: #6b7280;

        &:hover {
          background: #6b7280;
          color: white;
          transform: scale(1.1);
        }
      }
    }
  }
}

// Enhanced Table Styling
.table-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  .table-wrapper {
    ::v-deep .vgt-table {
      border: none;

      thead {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

        th {
          border: none;
          font-weight: 600;
          color: #1f2937;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.8rem;
          padding: 1.5rem 1rem;
        }
      }

      tbody {
        tr {
          border-bottom: 1px solid #f1f2f6;
          transition: all 0.3s ease;

          &:hover {
            background: #f8f9fa;
          }

          td {
            border: none;
            padding: 1.5rem 1rem;
            vertical-align: middle;
          }
        }
      }
    }

    .user-cell {
      display: flex;
      align-items: center;

      .user-avatar {
        margin-right: 1rem;

        .avatar-circle-small {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #C62828 0%, #0a3370 100%);
          display: flex;
          align-items: center;
          justify-content: center;

          .avatar-initials-small {
            color: white;
            font-weight: 600;
            font-size: 0.9rem;
          }
        }
      }

      .user-info {
        .user-name {
          font-weight: 600;
          color: #2c2c54;
          display: block;
        }

        .user-role {
          font-size: 0.8rem;
          color: #8b92b2;
        }
      }
    }

    .contact-cell {
      display: flex;
      align-items: center;

      .contact-icon {
        color: #8b92b2;
        margin-right: 0.5rem;
      }
    }

    .repository-cell {
      .repository-tag {
        background: rgba(102, 126, 234, 0.1);
        color: #C62828;
        padding: 0.3rem 0.8rem;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
      }
    }

    .status-cell {
      .status-badge-table {
        padding: 0.4rem 1rem;
        border-radius: 20px;
        font-size: 0.8rem;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
        }

        &.active {
          background: rgba(86, 202, 0, 0.1);
          color: #56ca00;

          .status-dot {
            background: #56ca00;
          }
        }

        &.inactive {
          background: rgba(107, 114, 128, 0.1);
          color: #6b7280;

          .status-dot {
            background: #6b7280;
          }
        }
      }
    }

    .action-cell {
      .action-buttons {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;

        .action-btn-table-text {
          border-radius: 6px;
          padding: 0.375rem 0.75rem;
          font-size: 0.75rem;
          font-weight: 600;
          transition: all 0.3s ease;
          text-decoration: none;

          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          }

          &.toggle {
            &:hover {
              transform: translateY(-1px);
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }
          }
        }

        // Keep old circular button styles for backward compatibility
        .action-btn-table {
          width: 35px;
          height: 35px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          text-decoration: none;

          &.view {
            background: rgba(102, 126, 234, 0.1);
            color: #C62828;

            &:hover {
              background: #C62828;
              color: white;
            }
          }

          &.edit {
            background: rgba(255, 193, 7, 0.1);
            color: #ffc107;

            &:hover {
              background: #ffc107;
              color: white;
            }
          }

          &.delete {
            background: rgba(107, 114, 128, 0.1);
            color: #6b7280;

            &:hover {
              background: #6b7280;
              color: white;
            }
          }
        }
      }
    }

    .table-pagination {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem 2rem;
      background: #f8f9fa;
      border-top: 1px solid #e9ecef;

      .pagination-info {
        color: #8b92b2;
        font-size: 0.9rem;
      }

      .pagination-controls {
        display: flex;
        align-items: center;

        .per-page-select {
          border: 2px solid #e9ecef;
          border-radius: 8px;
          background: white;
        }

        .modern-pagination {
          ::v-deep .page-link {
            border: 2px solid #e9ecef;
            border-radius: 8px;
            margin: 0 0.25rem;
            color: #C62828;
            font-weight: 600;

            &:hover {
              background: #C62828;
              border-color: #C62828;
              color: white;
            }
          }

          ::v-deep .page-item.active .page-link {
            background: #C62828;
            border-color: #C62828;
          }
        }
      }
    }
  }
}

// Enhanced Empty State
.empty-state {
  text-align: center;
  padding: 4rem 2rem;

  .empty-state-content {
    max-width: 500px;
    margin: 0 auto;
  }

  .empty-state-illustration {
    margin-bottom: 2rem;

    .illustration-circle {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto;

      .illustration-icon {
        color: #C62828;
        opacity: 0.6;
      }
    }
  }

  .empty-state-title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #2c2c54;
    margin-bottom: 1rem;
  }

  .empty-state-description {
    color: #8b92b2;
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 2rem;
  }

  .empty-state-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
  }
}

// Responsive Design
@media (max-width: 1200px) {
  .managers-grid .row > div[class^="col-"] {
    max-width: 50%;
    flex: 0 0 50%;
  }
}
@media (max-width: 992px) {
  .managers-grid .row > div[class^="col-"] {
    max-width: 100%;
    flex: 0 0 100%;
  }
  .stats-card {
    padding: 1rem;
  }
  .search-filter-section .card-body {
    padding: 1rem;
  }
  .table-wrapper {
    padding: 0.5rem;
  }
}
@media (max-width: 768px) {
  .managers-index {
    padding: 0.5rem;
  }
  .page-header {
    flex-direction: column;
    .page-title {
      font-size: 1.5rem;
    }
    .btn-modern {
      width: 100%;
      margin-top: 1rem;
      padding: 0.75rem 1rem;
    }
  }
  .stats-card {
    padding: 1rem;
    margin-bottom: 1rem;
  }
  .search-filter-section {
    .card-body {
      padding: 0.5rem;
    }
    .b-row {
      flex-direction: column !important;
      .b-col {
        width: 100% !important;
        margin-bottom: 1rem;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }
    }
    .action-buttons-group {
      flex-direction: column;
      gap: 0.5rem;
      .btn-modern-outline,
      .btn-modern {
        width: 100%;
        margin-bottom: 0.5rem;
      }
      .ml-2 {
        margin-left: 0 !important;
      }
    }
    .card-header {
      padding: 1rem;
      .card-title {
        font-size: 1rem;
      }
    }
  }
  .managers-grid .row {
    flex-direction: column;
    > div[class^="col-"] {
      max-width: 100%;
      flex: 0 0 100%;
      margin-bottom: 1rem;
    }
  }
  .manager-card-modern {
    border-radius: 12px;
    .card-header-modern,
    .card-body-modern,
    .card-actions-modern {
      padding: 1rem;
    }
    .manager-avatar .avatar-circle {
      width: 40px;
      height: 40px;
      .avatar-initials {
        font-size: 1rem;
      }
    }
  }
  .table-container {
    border-radius: 12px;
    .table-wrapper {
      padding: 0.5rem;
      overflow-x: auto;
      ::v-deep .vgt-table {
        min-width: 600px;
        font-size: 0.9rem;
        th, td {
          padding: 0.75rem 0.5rem;
        }
      }
    }
    .table-pagination {
      flex-direction: column;
      align-items: flex-start;
      padding: 1rem;
      .pagination-controls {
        flex-direction: column;
        gap: 0.5rem;
        .per-page-select {
          margin-bottom: 0.5rem;
        }
      }
    }
  }
  .empty-state {
    padding: 2rem 0.5rem;
    .empty-state-content {
      max-width: 100%;
    }
    .empty-state-illustration .illustration-circle {
      width: 80px;
      height: 80px;
    }
    .empty-state-title {
      font-size: 1.2rem;
    }
    .empty-state-description {
      font-size: 1rem;
    }
    .empty-state-actions {
      flex-direction: column;
      gap: 0.5rem;
      .btn-modern,
      .btn-modern-outline {
        width: 100%;
        max-width: 250px;
      }
    }
  }
  .loading-container {
    padding: 2rem 0;
    .modern-spinner {
      width: 48px;
      height: 48px;
      .spinner-ring {
        width: 32px;
        height: 32px;
        margin: 4px;
        border-width: 4px;
      }
    }
    .loading-text {
      font-size: 1rem;
    }
  }
}

// Extra small devices
@media (max-width: 480px) {
  .managers-index {
    padding: 0.25rem;
  }
  .page-header .page-title {
    font-size: 1.1rem;
  }
  .manager-card-modern .card-header-modern,
  .manager-card-modern .card-body-modern,
  .manager-card-modern .card-actions-modern {
    padding: 0.5rem;
  }
  .manager-card-modern .manager-avatar .avatar-circle {
    width: 32px;
    height: 32px;
    .avatar-initials {
      font-size: 0.8rem;
    }
  }
  .table-container .table-wrapper {
    ::v-deep .vgt-table {
      min-width: 400px;
      font-size: 0.8rem;
      th, td {
        padding: 0.5rem 0.25rem;
      }
    }
  }
  .empty-state .empty-state-illustration .illustration-circle {
    width: 48px;
    height: 48px;
  }
}

/* Password Change Modal Premium Styling */
#password-change-modal-manager {
  ::v-deep .modal-content {
    border-radius: 16px;
    border: none;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  }

  ::v-deep .modal-header {
    border-bottom: 1px solid #e2e8f0;
    padding: 1.5rem;
  }

  ::v-deep .modal-body {
    padding: 1.5rem;
  }

  .password-icon-wrapper-modal {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #e2eefa 0%, #f2f7fc 100%);
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);
  }

  .form-label-premium {
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.75rem;
    margin-bottom: 0.5rem;
  }

  .generate-password-btn-centered {
    padding: 0.75rem 2rem;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.9375rem;
    border: 2px solid #C62828;
    color: #C62828;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(13, 67, 153, 0.1);

    &:hover {
      background: linear-gradient(135deg, #C62828 0%, #0a3370 100%);
      color: #ffffff;
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
      border-color: #C62828;
    }

    &:active {
      transform: translateY(0);
    }
  }

  .premium-input-group {
    .form-control {
      border: 1px solid #e2e8f0;
      padding: 0.75rem 1rem;
      border-radius: 8px 0 0 8px;
      font-size: 0.9375rem;
      transition: all 0.2s ease;

      &:focus {
        border-color: #C62828;
        box-shadow: 0 0 0 3px rgba(13, 67, 153, 0.1);
      }

      &.is-valid {
        border-color: #28a745;

        &:focus {
          box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.15);
        }
      }

      &.is-invalid {
        border-color: #dc3545;

        &:focus {
          box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
        }
      }
    }

    .input-group-append {
      .password-toggle-btn {
        border: 1px solid #e2e8f0;
        border-left: none;
        border-radius: 0 8px 8px 0;
        background: #f8fafc;
        color: #64748b;
        padding: 0.75rem 1rem;
        transition: all 0.2s ease;

        &:hover {
          background: #C62828;
          color: #ffffff;
          border-color: #C62828;
        }
      }
    }
  }

  .password-strength-wrapper {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 0.75rem;

    .strength-label {
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      font-size: 0.7rem;
    }

    .strength-text {
      font-weight: 700;
      font-size: 0.75rem;

      &.text-danger {
        color: #dc3545 !important;
      }

      &.text-warning {
        color: #ffc107 !important;
      }

      &.text-info {
        color: #17a2b8 !important;
      }

      &.text-success {
        color: #28a745 !important;
      }
    }

    .premium-progress {
      border-radius: 10px;
      background: #e2e8f0;
      overflow: hidden;

      ::v-deep .progress-bar {
        border-radius: 10px;
        transition: width 0.3s ease;
      }
    }
  }

  .premium-btn-secondary {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    border: 1px solid #e2e8f0;
    color: #64748b;
    background: #ffffff;
    transition: all 0.2s ease;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      color: #475569;
      transform: translateY(-1px);
    }
  }

  .premium-btn-primary {
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    background: linear-gradient(135deg, #C62828 0%, #0a3370 100%);
    border: none;
    color: #ffffff;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(13, 67, 153, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }

    &.not-allowed,
    &:disabled {
      opacity: 0.6 !important;
      cursor: not-allowed !important;
      pointer-events: auto !important;
    }

    &.btn-saving {
      animation: pulse-save 1.5s ease-in-out infinite;
    }
  }
}

@keyframes pulse-save {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(13, 67, 153, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(13, 67, 153, 0);
  }
}

/* Validation message animation */
.validation-message-animate {
  animation: fadeInSlide 0.3s ease-out;
}

@keyframes fadeInSlide {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
